<template>
  <div class="tbl tbl_col">
    <table :class="{ loading_md: isLoading }">
      <caption>
        {{
          tableData.caption
        }}
      </caption>
      <colgroup>
        <col v-for="col in tableData.columns" :key="col.key" :style="{ width: col.width }" />
      </colgroup>
      <thead>
        <tr>
          <th v-for="col in tableData.columns" :key="col.key" scope="col">
            {{ col.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <slot v-if="tableData.list.length > 0" />
        <tr v-else>
          <td :colspan="tableData.columns.length" class="ta_c">
            {{ emptyText ?? $text("table.emptyMessage") }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <BasePagination
    v-if="page && serverOptions && api"
    v-model:page="serverOptions.page"
    v-model:api="handlePagination"
    :serverOptions="serverOptions"
  />
</template>

<script setup lang="ts">
import { watch, onMounted, onUnmounted, ref, nextTick, readonly } from "vue";
import BasePagination from "@/common/base-components/client/BasePagination.vue";
import { global, i18n } from "@/common/plugins/global";

/* ------------------------------------------------------------
                          Vue Instance & Global Properties
   ------------------------------------------------------------ */
const vm = global();
const { $text } = i18n();

/* ------------------------------------------------------------
                          Props
   ------------------------------------------------------------ */
const props = defineProps<{
  page?: boolean; // 페이징 여부 (기본값: false) ( Using Paging )
  api?: (params: Record<string, unknown>) => Promise<unknown>; // 데이터 조회 API ( Data Get API )
  searchParams?: Record<string, unknown>; // 검색 파라미터 ( Search Parameters )
  debounceMs?: number; // 디바운스 시간 ( Debounce Time )
  emptyText?: string; // 빈 테이블 메시지 ( empty Table Message )
  autoLoad?: boolean; // 자동 로딩 여부 (기본값: true) ( Auto Loading )
  isResetting?: boolean; // 초기화 중인지 여부 ( Resetting Flag )
}>();

/* ------------------------------------------------------------
                          Model
   ------------------------------------------------------------ */
const tableData = defineModel<{
  caption?: string; // 테이블 제목 ( Table Caption )
  columns: Array<{
    key: string;
    label: string;
    width?: string;
  }>;
  list: Array<unknown>;
}>("tableData", { required: true });

const serverOptions = defineModel<{
  page: number;
  totalCount: number;
  rowsPerPage: number;
  sortBy: string[];
  sortType: string[];
}>("serverOptions", { required: false });

/* ------------------------------------------------------------
                          Variable Declarations
   ------------------------------------------------------------ */
// 로딩 상태 및 초기화 플래그
const isLoading = ref(false);
const isInitialized = ref(false);
let debounceTimer: number | null = null;

/* ------------------------------------------------------------
                          Methods
   ------------------------------------------------------------ */
/** 페이징 처리를 위한 데이터 조회 */
const handlePagination = ref(async (params?: Record<string, unknown>) => {
  if (!props.api || !serverOptions.value || isLoading.value) return;

  isLoading.value = true;

  try {
    // searchParams와 serverOptions를 합쳐서 파라미터 구성
    const finalParams = {
      ...(props.searchParams || {}),
      ...(params || vm.$serial(serverOptions.value)),
    };

    const response = await props.api(finalParams);

    if (Array.isArray(response)) {
      tableData.value.list = response;

      // totalCount 자동 계산
      if (response.length > 0) {
        const firstItem = response[0] as unknown as { totalCount: number };
        if (firstItem.totalCount !== undefined) {
          serverOptions.value.totalCount = firstItem.totalCount;
        }
      } else {
        serverOptions.value.totalCount = 0;
      }
    }

    return response;
  } catch (error) {
    console.error($text("message.apiError"), error);
    return [];
  } finally {
    isLoading.value = false;
  }
});

/** Debounced 검색 함수 */
const debouncedSearch = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(() => {
    if (serverOptions.value) {
      serverOptions.value.page = 1;
    }
    handlePagination.value();
  }, props.debounceMs || 300);
};

/** searchParams가 실제로 변경되었는지 확인 */
const hasSearchParamsChanged = (
  newParams: Record<string, unknown> | undefined,
  oldParams: Record<string, unknown> | undefined
) => {
  if (!newParams && !oldParams) return false;
  if (!newParams || !oldParams) return true;

  const newKeys = Object.keys(newParams);
  const oldKeys = Object.keys(oldParams);

  if (newKeys.length !== oldKeys.length) return true;

  return newKeys.some((key) => newParams[key] !== oldParams[key]);
};

/* ------------------------------------------------------------
                          watch
   ------------------------------------------------------------ */
/** searchParams 변경 시 debounced 검색 실행 */
watch(
  () => props.searchParams,
  (newParams, oldParams) => {
    // 초기화 완료 후에만 동작
    if (!isInitialized.value) return;

    // 초기화 중이면 검색하지 않음
    if (props.isResetting) return;

    // 실제 변경이 있을 때만 검색
    if (hasSearchParamsChanged(newParams, oldParams)) {
      debouncedSearch();
    }
  },
  { deep: true }
);

// 페이지 변경 시 즉시 검색 (debounce 없음)
watch(
  () => serverOptions.value?.page,
  (newPage, oldPage) => {
    if (isInitialized.value && newPage !== oldPage) {
      handlePagination.value();
    }
  }
);

/* ------------------------------------------------------------
                          Lifecycle
   ------------------------------------------------------------ */
/** 컴포넌트 마운트 시 초기 데이터 로드 */
onMounted(async () => {
  if (props.api && props.autoLoad !== false) {
    // autoLoad가 명시적으로 false가 아니면 자동 로딩
    await nextTick(); // DOM 업데이트 완료 대기
    await handlePagination.value();
  }
  isInitialized.value = true;
});

/** 컴포넌트 언마운트 시 정리 */
onUnmounted(() => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
});

/* ------------------------------------------------------------
                          Expose
   ------------------------------------------------------------ */
defineExpose({
  fnSearch: () => {
    if (serverOptions.value) {
      serverOptions.value.page = 1; // 첫 페이지로 이동
    }
    handlePagination.value();
  },
  refresh: () => {
    handlePagination.value(); // 현재 페이지에서 새로고침
  },
  isLoading: readonly(isLoading), // loading 상태 노출
});
</script>
